/**
 * 視点（ズーム位置）の座標変換（エンジン共通層・純関数）。
 *
 * 共有 URL でズーム位置を再現する（FR-LINK-03）には、画面寸法に依存しない表現が要る。
 * 可視範囲の中心を本ローカル座標で書き下すと
 *
 *   centerX = book.width / 2  - (pan.x + shift) / zoom
 *   centerY = book.height / 2 -  pan.y          / zoom
 *
 * となり container が相殺される — つまり「中心」は zoom と pan だけで決まる。
 * これが別の画面サイズで同じ場所を再現できる根拠。
 *
 * ただし本ローカル座標のままでは移植できない。本の幅は box.width * halves であり、
 * halves は resolveMode() がコンテナ幅から決めるので、共有元が見開き・共有先が単ページ
 * だと本の定義自体が変わってしまう。そこで正規化は「ページ基準」で行う
 * （region.ts の PageRegion と同じ規約 = ページ左上原点の 0〜1）。
 */
import { visibleBox, type Box, type Rect } from './region';

/** 本ローカル座標に置かれたページ 1 枚。 */
export interface PageSlotRect {
  page: number;
  rect: Rect;
}

/** 可視範囲の中心（本ローカル座標）。定義点を 1 つに保つため visibleBox から導く。 */
export function centerOf(
  container: Box,
  book: Box,
  zoom: number,
  pan: { x: number; y: number },
  shift: number,
): { x: number; y: number } {
  const visible = visibleBox(container, book, zoom, pan, shift);
  return { x: visible.x + visible.w / 2, y: visible.y + visible.h / 2 };
}

/**
 * 本ローカル座標の点を、それが乗るページの正規化座標へ写す。
 * どのページにも乗らないときは最も近いページへクランプする（本の外を指す共有 URL でも
 * 必ず何かページを返す — 呼び出し側が null 分岐を増やさずに済む）。
 */
export function toPageLocal(
  point: { x: number; y: number },
  slots: readonly PageSlotRect[],
): { page: number; x: number; y: number } | null {
  let best: PageSlotRect | null = null;
  let bestDistance = Infinity;
  for (const slot of slots) {
    const distance = rectDistance(point, slot.rect);
    if (distance < bestDistance) {
      best = slot;
      bestDistance = distance;
    }
  }
  if (!best) return null;
  return {
    page: best.page,
    x: clamp01((point.x - best.rect.x) / best.rect.w),
    y: clamp01((point.y - best.rect.y) / best.rect.h),
  };
}

/**
 * 逆変換 — 指定ページ内の正規化位置を画面中心に置く pan（クランプ前）。
 * 呼び出し側でエンジンの clampPan を必ず通すこと。
 */
export function panForCenter(
  book: Box,
  rect: Rect,
  x: number,
  y: number,
  zoom: number,
  shift: number,
): { x: number; y: number } {
  return {
    x: (book.width / 2 - (rect.x + x * rect.w)) * zoom - shift,
    y: (book.height / 2 - (rect.y + y * rect.h)) * zoom,
  };
}

/** 点と矩形の距離の二乗（内側なら 0）。 */
function rectDistance(point: { x: number; y: number }, rect: Rect): number {
  const dx = Math.max(rect.x - point.x, 0, point.x - (rect.x + rect.w));
  const dy = Math.max(rect.y - point.y, 0, point.y - (rect.y + rect.h));
  return dx * dx + dy * dy;
}

function clamp01(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}
